import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { Button, FieldError, HelperText, Input, Label } from '@/components/ui';
import { AuthShell } from '@/components/AuthShell';
import { partnerApi } from '@/lib/api-affiliate';

const schema = z.object({ email: z.string().email('Некорректный email') });
type FormValues = z.infer<typeof schema>;

export function ResetPasswordRequestPage() {
  const [done, setDone] = useState(false);
  const reset = partnerApi.auth.useResetRequest();
  const { register, handleSubmit, formState } = useForm<FormValues>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (v: FormValues) => {
    await reset.mutateAsync(v);
    setDone(true);
  };

  if (done) {
    return (
      <AuthShell>
        <div className="flex flex-col items-center text-center">
          <div className="w-14 h-14 rounded-full bg-[var(--accent-soft)] grid place-items-center text-[var(--accent-bright)] mb-4">
            <Mail size={24} />
          </div>
          <h1 className="text-[22px] font-semibold mb-2 text-[var(--text)] tracking-tight">
            Письмо отправлено
          </h1>
          <p className="text-sm text-[var(--text-muted)] mb-2 leading-relaxed">
            Если такой email зарегистрирован, мы прислали ссылку для сброса пароля. Проверьте входящие.
          </p>
          <p className="text-xs text-[var(--text-quiet)] mb-6">
            Не пришло письмо? Проверьте папку «Спам».
          </p>
          <Link to="/login" className="block w-full">
            <Button variant="outline" className="w-full">
              К входу
            </Button>
          </Link>
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Сброс пароля"
      subtitle="Введите email — пришлём ссылку для сброса"
      footer={
        <Link to="/login" className="text-[var(--text-muted)] hover:text-[var(--text)]">
          ← Назад ко входу
        </Link>
      }
    >
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            autoComplete="email"
            placeholder="you@example.com"
            {...register('email')}
          />
          <FieldError message={formState.errors.email?.message} />
          <HelperText>Используйте email, с которым регистрировались</HelperText>
        </div>
        <Button
          type="submit"
          loading={reset.isPending}
          className="w-full"
          variant="gradient"
          size="lg"
        >
          {reset.isPending ? 'Отправка…' : 'Прислать ссылку'}
        </Button>
      </form>
    </AuthShell>
  );
}
